/**
 * LoggrManager.ts - LoggrManager
 *
 * CHANGE HISTORY:
 * - 251226_0906 [active] danielbeer: Remove @ts-nocheck and fix all type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { Session } from './models/Session.js';
import { ActivitySlug } from './models/ActivitySlug.js';
import { LogEntry, LogLevel, LogLevelNames } from './models/LogEntry.js';
import { ConsoleAdapter } from './adapters/ConsoleAdapter.js';
import { SyslogAdapter } from './adapters/SyslogAdapter.js';
import { StructuredLogAdapter } from './adapters/StructuredLogAdapter.js';
import { IntegratrAdapter } from './adapters/IntegratrAdapter.js';
import { SessionFileManager } from './utils/SessionFileManager.js';

interface LoggrManagerOptions {
  sessionId?: string;
  userId?: string;
  project?: string;
  projectPath?: string;
  adapters?: any[];
  minLevel?: number;
  minLogLevel?: number;
  context?: Record<string, any>;
  actr?: any; // Actr instance
  storagePrefix?: string;
  autoSave?: boolean;
  promptForUser?: boolean;
  sessionsDir?: string;
  historyDir?: string;
  autoUpdate?: boolean;
  integratr?: any;
  services?: any[];
  [key: string]: any;
}

interface LogFilter {
  level?: number;
  category?: string;
  limit?: number;
  sessionId?: string;
  type?: string;
  userId?: string;
  startDate?: string | Date;
  endDate?: string | Date;
}

interface SessionOptions {
  description?: string;
  type?: string;
  context?: Record<string, any>;
}

interface LogOptions {
  error?: Error;
  tags?: string[];
  metadata?: Record<string, any>;
  sessionId?: string;
  activityId?: string;
  type?: string;
  project?: string;
  projectPath?: string;
  [key: string]: any;
}

/**
 * LoggrManager class
 */
export class LoggrManager {
  sessionId?: string;
  userId?: string;
  project?: string;
  projectPath?: string;
  adapters: any[] = [];
  minLevel: number = 0;
  context: Record<string, any> = {};
  private actr;
  private options: LoggrManagerOptions;
  private sessions: Map<string, Session>;
  private activities: Map<string, ActivitySlug>;
  private currentSession: Session | null;
  private listeners: Set<any>;
  private userInfo;
  private logEntries: LogEntry[];
  private minLogLevel: number;
  private packageLevels: Map<string, number>;
  private functionLevels: Map<string, number>;
  private sessionFileManager: SessionFileManager;

  constructor(options: LoggrManagerOptions = {}) {
    this.actr = options.actr;
    this.options = {
      storagePrefix: options.storagePrefix || 'loggr',
      autoSave: options.autoSave !== false,
      userId: options.userId || undefined,
      promptForUser: options.promptForUser !== false,
      ...options,
    };

    this.sessions = new Map(); // sessionId -> Session
    this.activities = new Map(); // activityId -> ActivitySlug
    this.currentSession = null;
    this.listeners = new Set();
    this.userInfo = null;

    // Production logging
    this.logEntries = []; // Recent log entries
    this.adapters = []; // Log adapters
    this.minLogLevel = options.minLogLevel !== undefined ? options.minLogLevel : LogLevel.INFO;

    // Hierarchical log level control
    this.packageLevels = new Map(); // package -> LogLevel
    this.functionLevels = new Map(); // 'package.function' -> LogLevel

    // Initialize from options
    if (options) {
      Object.entries(options).forEach(([pkg, level]) => {
        this.packageLevels.set(pkg, level);
      });
    }
    if (options) {
      Object.entries(options).forEach(([fn, level]) => {
        this.functionLevels.set(fn, level);
      });
    }

    // Load from environment variables (LOGGR_LEVEL_<PACKAGE>=DEBUG)
    this.loadLevelsFromEnv();

    // Session file management
    this.sessionFileManager = new SessionFileManager({
      sessionsDir: options.sessionsDir || 'docs/sessions',
      historyDir: options.historyDir || 'docs/history',
      autoUpdate: options.autoUpdate !== false,
    });

    // Initialize adapters
    if (options.adapters && options.adapters.length > 0) {
      // Use provided adapters only
      options.adapters.forEach((adapter) => this.addAdapter(adapter));
    } else {
      // Initialize default adapters
      if (options) {
        this.addAdapter(
          new ConsoleAdapter({
            minLevel: options.minLevel || LogLevel.INFO,
          }),
        );
      }

      if (options) {
        this.addAdapter(new SyslogAdapter(options));
      }

      if (options) {
        this.addAdapter(new StructuredLogAdapter(options));
      }

      if (options.enableIntegratr && options) {
        this.addAdapter(
          new IntegratrAdapter({
            integratr: options.integratr,
            services: options.services || [],
          }),
        );
      }
    }

    // Load from storage
    this.loadFromStorage();

    // Load user info if available
    if (this.options.userId || this.options) {
      this.loadUserInfo();
    }

    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }

  /**
   * Register Loggr actions with Actr
   *
   * ARCHITECTURE NOTE: Loggr uses ACTIONS-ONLY pattern
   *
   * Why this is different from notifyr/groupr:
   * 1. Write-Heavy Operations: Logging is extremely write-intensive
   *    - Hundreds/thousands of log entries per second
   *    - Session tracking on every user action
   *    - Activity logging throughout development
   *    - CRUD overhead would kill performance
   *
   * 2. External Integrations: Logs go to external systems
   *    - Syslog, ELK, Splunk, Datadog, etc.
   *    - OpCentral doesn't own the log data long-term
   *    - External systems are source of truth for production logs
   *    - No need for OpCentral CRUD
   *
   * 3. Ephemeral Data: Logs are temporary
   *    - Recent logs kept in memory (circular buffer)
   *    - Session files written to disk (not database)
   *    - Old logs archived/deleted automatically
   *    - No need for full CRUD lifecycle
   *
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For logging operations (log, startSession, logActivity, etc.)
   * - No CRUD: Logs written to external systems, not stored in Actr
   * - File-based: Session tracking uses file system, not database
   *
   * Compare to other patterns:
   * - permittr: Actions-Only (performance-critical, in-memory cache)
   * - authr: Hybrid (actions + selective CRUD for audit)
   * - loggr: Actions-Only (write-heavy, external integrations, ephemeral)
   *
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/loggr/README.md "Actr Integration" section
   */
  private registerActions() {
    if (!this.actr) return;

    // log action (write-heavy operation)
    this.actr.register('loggr:log', {
      handler: async (params) => {
        return await this.log(params.level, params.message, params);
      },
      description: 'Log message',
    });

    // startSession action
    this.actr.register('loggr:start-session', {
      handler: async (params) => {
        return await this.startSession(params);
      },
      description: 'Start logging session',
    });

    // logActivity action (high-frequency)
    this.actr.register('loggr:log-activity', {
      handler: async (params) => {
        return await this.logActivity(params);
      },
      description: 'Log user activity',
    });

    // endSession action
    this.actr.register('loggr:end-session', {
      handler: async () => {
        return await this.endSession();
      },
      description: 'End logging session',
    });

    // exportTimeline action - TODO: implement exportTimeline method
    // this.actr.register('loggr:export-timeline', {
    //   handler: async (params) => {
    //     return this.exportTimeline(params);
    //   },
    //   description: 'Export activity timeline'
    // });

    // Note: No CRUD operations - logs are written to external systems
    // (syslog, ELK, Splunk, etc.) and session files are managed via file system.
  }

  /**
   * Add log adapter
   */
  addAdapter(adapter): void {
    this.adapters.push(adapter);
  }

  /**
   * Remove log adapter
   */
  removeAdapter(adapter): void {
    const index = this.adapters.indexOf(adapter);
    if (index !== -1) {
      this.adapters.splice(index, 1);
    }
  }

  /**
   * Load log levels from environment variables
   * Supports: LOGGR_LEVEL_<PACKAGE>=DEBUG, LOGGR_LEVEL_<PACKAGE>_<FUNCTION>=TRACE
   */
  private loadLevelsFromEnv(): void {
    if (typeof process === 'undefined' || !process.env) return;

    Object.keys(process.env).forEach((key) => {
      if (key.startsWith('LOGGR_LEVEL_')) {
        const name = key.replace('LOGGR_LEVEL_', '').toLowerCase();
        const levelName = process.env[key]?.toUpperCase();
        const level = levelName ? LogLevel[levelName as keyof typeof LogLevel] : undefined;

        if (level !== undefined) {
          if (name.includes('_')) {
            // Function level: LOGGR_LEVEL_AUTHR_LOGIN=TRACE
            const fnName = name.replace('_', '.');
            this.functionLevels.set(fnName, level);
          } else {
            // Package level: LOGGR_LEVEL_AUTHR=DEBUG
            this.packageLevels.set(name, level);
          }
        }
      }
    });
  }

  /**
   * Get effective log level for a category/function
   * Inheritance: function → package → global
   */
  private getEffectiveLevel(category: string): number {
    // Check function level first (most specific)
    if (this.functionLevels.has(category)) {
      return this.functionLevels.get(category)!;
    }

    // Check package level
    const packageName = category.split('.')[0];
    if (this.packageLevels.has(packageName)) {
      return this.packageLevels.get(packageName)!;
    }

    // Fall back to global level
    return this.minLogLevel;
  }

  /**
   * Check if log should be written based on hierarchical levels
   */
  private shouldLog(level: number, category: string): boolean {
    const effectiveLevel = this.getEffectiveLevel(category);
    return level >= effectiveLevel;
  }

  /**
   * Set package-level log level (runtime update)
   */
  setPackageLevel(packageName: string, level: number): void {
    this.packageLevels.set(packageName.toLowerCase(), level);
  }

  /**
   * Set function-level log level (runtime update)
   */
  setFunctionLevel(functionName: string, level: number): void {
    this.functionLevels.set(functionName.toLowerCase(), level);
  }

  /**
   * Clear package-level override (revert to global)
   */
  clearPackageLevel(packageName: string): void {
    this.packageLevels.delete(packageName.toLowerCase());
  }

  /**
   * Clear function-level override (revert to package/global)
   */
  clearFunctionLevel(functionName: string): void {
    this.functionLevels.delete(functionName.toLowerCase());
  }

  /**
   * Get current log level configuration
   */
  getLogLevelConfig() {
    return {
      global: this.minLogLevel,
      packages: Object.fromEntries(this.packageLevels),
      functions: Object.fromEntries(this.functionLevels),
    };
  }

  /**
   * Log message (production logging)
   * Now with hierarchical level checking
   *
   * Supports two calling conventions:
   *   log(level, message, category?, context?)   — explicit category string
   *   log(level, message, context?)              — context as second arg (category defaults to 'general')
   */
  log(
    level: number,
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ): LogEntry | undefined {
    // Handle common pattern: log(level, message, { data }) where second arg is context, not category
    let category: string;
    if (typeof categoryOrContext === 'string') {
      category = categoryOrContext;
    } else if (categoryOrContext && typeof categoryOrContext === 'object') {
      category = 'general';
      context = categoryOrContext as LogOptions;
    } else {
      category = 'general';
    }

    // Check if this log should be written based on hierarchical levels
    if (!this.shouldLog(level, category)) {
      return undefined; // Skip this log
    }
    const logEntry = new LogEntry({
      level: level as any,
      message,
      category,
      userId: this.options.userId,
      sessionId: this.currentSession?.id,
      context,
      error: context.error,
      tags: context.tags || [],
      metadata: context.metadata || {},
    });

    // Store recent entries
    this.logEntries.push(logEntry);
    if (this.logEntries.length > 1000) {
      this.logEntries.shift();
    }

    // Send to adapters
    this.adapters.forEach((adapter) => {
      try {
        adapter.write(logEntry);
      } catch (error) {
        console.error('Adapter error:', error);
      }
    });

    this.notifyListeners('log_entry', { logEntry });

    return logEntry;
  }

  /**
   * Convenience logging methods
   *
   * Support two calling conventions:
   *   logger.info('message', 'category', { context })
   *   logger.info('message', { context })              — category defaults to 'general'
   */
  trace(
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ) {
    return this.log(LogLevel.TRACE, message, categoryOrContext, context);
  }

  debug(
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ) {
    return this.log(LogLevel.DEBUG, message, categoryOrContext, context);
  }

  info(
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ) {
    return this.log(LogLevel.INFO, message, categoryOrContext, context);
  }

  warn(
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ) {
    return this.log(LogLevel.WARN, message, categoryOrContext, context);
  }

  error(
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ) {
    return this.log(LogLevel.ERROR, message, categoryOrContext, context);
  }

  fatal(
    message: string,
    categoryOrContext: string | LogOptions = 'general',
    context: LogOptions = {},
  ) {
    return this.log(LogLevel.FATAL, message, categoryOrContext, context);
  }

  /**
   * Get recent log entries
   */
  getRecentLogs(filter: LogFilter = {}) {
    let logs = [...this.logEntries];

    if (filter.level !== undefined) {
      logs = logs.filter((log) => log.level >= filter.level!);
    }

    if (filter.category) {
      logs = logs.filter((log) => log.category === filter.category);
    }

    if (filter.limit) {
      logs = logs.slice(-filter.limit);
    }

    return logs;
  }

  /**
   * Initialize user identity
   * Prompts for name if not set, or infers from system
   */
  initializeUser() {
    // Check if user info is stored
    const storedUser = this.loadUserInfo();

    if (storedUser) {
      this.userInfo = storedUser;
      this.options.userId = storedUser.id;
      console.log(`👋 Welcome back, ${storedUser.name}!`);
      return storedUser;
    }

    // Try to infer from system
    const systemUser = this.inferSystemUser();

    if (systemUser && !this.isGenericUser(systemUser)) {
      this.userInfo = {
        id: systemUser,
        name: systemUser,
        source: 'system',
        createdAt: new Date().toISOString(),
      };
      this.options.userId = systemUser;
      this.saveUserInfo(this.userInfo);
      console.log(`👋 Hello, ${systemUser}! (inferred from system)`);
      return this.userInfo;
    }

    // Prompt for user name
    console.log('⚠️  User identity not set. Please call loggr.setUser(name) to identify yourself.');
    console.log('   Example: loggr.setUser("John Doe")');

    // Use generic placeholder
    this.options.userId = 'unknown-user';

    return null;
  }

  /**
   * Detect current project/repository
   */
  detectProject() {
    // Try to get from options first
    if (this.options) {
      return {
        name: this.options.project,
        path: this.options.projectPath || process.cwd(),
      };
    }

    // In Node.js, try to detect from current directory
    if (typeof process !== 'undefined' && process.cwd) {
      const cwd = process.cwd();
      const parts = cwd.split('/');
      const projectName = parts[parts.length - 1]; // Last directory name

      return {
        name: projectName,
        path: cwd,
      };
    }

    // In browser, try to get from window location
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname;
      return {
        name: hostname,
        path: window.location.href,
      };
    }

    return {
      name: 'unknown-project',
      path: undefined,
    };
  }

  /**
   * Set user identity
   */
  setUser(name, additionalInfo = {}) {
    if (!name || typeof name !== 'string') {
      throw new Error('User name must be a non-empty string');
    }

    // Parse name to get first initial + last name
    const { shortId, fullName } = this.parseUserName(name);

    // Get machine identifier
    const machineId = this.getMachineIdentifier();

    this.userInfo = {
      id: shortId,
      name: fullName,
      fullName: fullName,
      shortId: shortId,
      machineId: machineId,
      source: 'manual',
      createdAt: new Date().toISOString(),
      ...additionalInfo,
    };

    this.options.userId = shortId;
    this.saveUserInfo(this.userInfo);

    console.log(`✅ User set: ${fullName} (${shortId})`);
    console.log(`💻 Machine: ${machineId}`);

    this.notifyListeners('user_set', { userInfo: this.userInfo });

    return this.userInfo;
  }

  /**
   * Parse user name to get first initial + last name
   * Examples:
   *   "John Doe" -> "jdoe"
   *   "Jane Smith" -> "jsmith"
   *   "Bob" -> "bob"
   */
  parseUserName(name) {
    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
      // Single name (e.g., "Bob")
      return {
        shortId: parts[0].toLowerCase(),
        fullName: name,
      };
    }

    // First initial + last name
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    const firstInitial = firstName[0].toLowerCase();
    const lastNameLower = lastName.toLowerCase();

    return {
      shortId: `${firstInitial}${lastNameLower}`,
      fullName: name,
    };
  }

  /**
   * Get machine identifier
   * Uses hostname or generates a unique ID
   */
  getMachineIdentifier() {
    // Try to get hostname
    if (typeof require !== 'undefined') {
      try {
        const os = require('os');
        return os.hostname();
      } catch (error) {
        // Fall through
      }
    }

    // Try environment variables
    if (typeof process !== 'undefined' && process.env) {
      const hostname = process.env.HOSTNAME || process.env.COMPUTERNAME || process.env.HOST;
      if (hostname) return hostname;
    }

    // Browser - use a stored machine ID
    if (typeof localStorage !== 'undefined') {
      let machineId = localStorage.getItem('loggr_machine_id');
      if (!machineId) {
        machineId = `machine-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('loggr_machine_id', machineId);
      }
      return machineId;
    }

    return 'unknown-machine';
  }

  /**
   * Validate user is an actual person
   * Checks if the name looks like a real person's name
   */
  validateUserName(name) {
    const parts = name.trim().split(/\s+/);

    // Should have at least first name
    if (parts.length === 0) {
      return {
        valid: false,
        reason: 'Name is empty',
      };
    }

    // Check for generic names
    const genericNames = [
      'user',
      'admin',
      'test',
      'demo',
      'guest',
      'system',
      'default',
      'cascade',
      'unknown',
    ];

    const lowerName = name.toLowerCase();
    if (genericNames.some((generic) => lowerName.includes(generic))) {
      return {
        valid: false,
        reason: 'Name appears to be generic (user, admin, test, etc.)',
        suggestion: 'Please use your actual name (e.g., "John Doe")',
      };
    }

    // Should have at least 2 characters
    if (name.length < 2) {
      return {
        valid: false,
        reason: 'Name is too short',
      };
    }

    // Looks valid
    return {
      valid: true,
      parsedName: this.parseUserName(name),
    };
  }

  /**
   * Get current user info
   */
  getUserInfo() {
    return this.userInfo;
  }

  /**
   * Infer user from system
   */
  inferSystemUser() {
    // Try environment variables
    if (typeof process !== 'undefined' && process.env) {
      return process.env.USER || process.env.USERNAME || process.env.LOGNAME || null;
    }

    // Browser context - no system user
    return null;
  }

  /**
   * Check if user is generic/placeholder
   */
  isGenericUser(userId): boolean {
    const genericPatterns = [
      /^user$/i,
      /^system$/i,
      /^admin$/i,
      /^test$/i,
      /^unknown/i,
      /^guest$/i,
      /^default$/i,
      /^cascade$/i, // AI assistant
    ];

    return genericPatterns.some((pattern) => pattern.test(userId));
  }

  /**
   * Load user info from storage
   */
  loadUserInfo() {
    try {
      if (typeof localStorage === 'undefined') return null;
      const stored = localStorage.getItem(`${this.options.storageKey}:userInfo`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
    return null;
  }

  /**
   * Save user info to storage
   */
  saveUserInfo(userInfo) {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(`${this.options.storageKey}:userInfo`, JSON.stringify(userInfo));
    } catch (error) {
      console.error('Error saving user info:', error);
    }
  }

  /**
   * Start new session
   */
  async startSession(
    description: string,
    type: string = 'development',
    context: Record<string, any> = {},
  ): Promise<any> {
    // Detect project if not provided
    const project = this.detectProject();

    const session = new Session({
      userId: this.options.userId,
      type,
      description,
      project: project.name,
      projectPath: project.path,
      context,
    });

    this.sessions.set(session.id, session);
    this.currentSession = session;

    this.saveToStorage();
    this.notifyListeners('session_started', { session });

    // Create session file
    await this.createSessionFile(session);

    // Log session start
    this.info(`Session started: ${description}`, 'session', {
      sessionId: session.id,
      type: type,
      project: project.name,
      projectPath: project.path,
    });

    console.log(`📂 Project: ${project.name}`);
    console.log(`📁 Path: ${project.path}`);

    return session;
  }

  /**
   * Create session file in docs/sessions/
   */
  async createSessionFile(session): Promise<any> {
    if (!this.sessionFileManager) return null;

    const activities = this.getSessionActivities(session.id);
    const result = await this.sessionFileManager.createSessionFile(
      session,
      this.userInfo,
      activities,
    );

    if (result) {
      console.log(`📄 Session file: ${result.filepath}`);
    }

    return result;
  }

  /**
   * Update session file
   */
  async updateSessionFile(): Promise<any> {
    if (!this.currentSession || !this.sessionFileManager) return null;

    const activities = this.getSessionActivities(this.currentSession.id);
    return await this.sessionFileManager.updateSessionFile({
      session: this.currentSession,
      userInfo: this.userInfo,
      activities,
    });
  }

  /**
   * End current session
   */
  async endSession(): Promise<any> {
    if (!this.currentSession) {
      return null;
    }

    this.currentSession.end();
    this.saveToStorage();

    // Update session file with final state
    await this.updateSessionFile();

    // Log session end
    this.info(`Session ended: ${this.currentSession.description}`, 'session', {
      sessionId: this.currentSession.id,
      duration: this.currentSession.getDuration(),
    });

    this.notifyListeners('session_ended', { session: this.currentSession });

    const endedSession = this.currentSession;
    this.currentSession = null;

    return endedSession;
  }

  /**
   * Get current session
   */
  getCurrentSession() {
    return this.currentSession;
  }

  /**
   * Log activity
   */
  async logActivity(
    description: string,
    type: string = 'action',
    context: Record<string, any> = {},
  ): Promise<any> {
    const activity = new ActivitySlug({
      userId: this.options.userId,
      sessionId: this.currentSession?.id,
      description,
      type,
      context,
    });

    this.activities.set(activity.id, activity);

    // Add to current session
    if (this.currentSession) {
      this.currentSession.addActivity(activity.id);

      // Update session file
      await this.updateSessionFile();
    }

    this.saveToStorage();
    this.notifyListeners('activity_logged', { activity });

    // Log activity
    this.debug(`Activity: ${description}`, 'activity', {
      activityId: activity.id,
      type: type,
    });

    return activity;
  }

  /**
   * Create document slug
   */
  createDocumentSlug(description: string, context: Record<string, any> = {}) {
    return this.logActivity(description, 'document', context);
  }

  /**
   * Create commit slug
   */
  createCommitSlug(description: string, context: Record<string, any> = {}) {
    return this.logActivity(description, 'commit', context);
  }

  /**
   * Get all sessions
   */
  getSessions(filter: LogFilter = {}) {
    let sessions = Array.from(this.sessions.values());

    // Filter by type
    if (filter.type) {
      sessions = sessions.filter((s) => s.type === filter.type);
    }

    // Filter by user
    if (filter.userId) {
      sessions = sessions.filter((s) => s.userId === filter.userId);
    }

    // Filter by date range
    if (filter.startDate) {
      sessions = sessions.filter((s) => new Date(s.startTime) >= new Date(filter.startDate!));
    }

    if (filter.endDate) {
      sessions = sessions.filter((s) => new Date(s.startTime) <= new Date(filter.endDate!));
    }

    // Sort by start time (newest first)
    sessions.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

    return sessions;
  }

  /**
   * Get session by ID
   */
  getSession(sessionId) {
    return this.sessions.get(sessionId);
  }

  /**
   * Get activities for session
   */
  getSessionActivities(sessionId) {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return [];
    }

    return session.activities.map((activityId) => this.activities.get(activityId)).filter(Boolean);
  }

  /**
   * Get all activities
   */
  getActivities(filter: LogFilter = {}) {
    let activities = Array.from(this.activities.values());

    // Filter by session
    if (filter.sessionId) {
      activities = activities.filter((a) => a.sessionId === filter.sessionId);
    }

    // Filter by type
    if (filter.type) {
      activities = activities.filter((a) => a.type === filter.type);
    }

    // Filter by user
    if (filter.userId) {
      activities = activities.filter((a) => a.userId === filter.userId);
    }

    // Sort by timestamp (newest first)
    activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return activities;
  }

  /**
   * Generate timeline
   */
  generateTimeline(filter: LogFilter = {}) {
    const sessions = this.getSessions(filter);
    const timeline: any[] = [];

    sessions.forEach((session) => {
      timeline.push({
        type: 'session_start',
        timestamp: session.startTime,
        session: session,
        description: `Started: ${session.description}`,
      });

      const activities = this.getSessionActivities(session.id);
      activities.forEach((activity) => {
        if (activity) {
          timeline.push({
            type: 'activity',
            timestamp: activity.timestamp,
            session: session,
            activity: activity,
            description: activity.description,
          });
        }
      });

      if (session.endTime) {
        timeline.push({
          type: 'session_end',
          timestamp: session.endTime,
          session: session,
          description: `Ended: ${session.description}`,
        });
      }
    });

    // Sort by timestamp
    timeline.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return timeline;
  }

  /**
   * Export session log
   */
  exportSessionLog(sessionId: string): any {
    const session = this.getSession(sessionId);
    if (!session) {
      return null;
    }

    const activities = this.getSessionActivities(sessionId);

    return {
      session: session.toJSON(),
      activities: activities.map((a) => a?.toJSON()).filter(Boolean),
      duration: session.getDuration(),
      activityCount: activities.length,
    };
  }

  /**
   * Export all sessions as markdown
   */
  exportAsMarkdown(): string {
    const sessions = this.getSessions();
    let markdown = `# Session Log\n\n`;
    markdown += `**Total Sessions**: ${sessions.length}\n\n`;
    markdown += `---\n\n`;

    sessions.forEach((session) => {
      const activities = this.getSessionActivities(session.id);
      const duration = Math.round(session.getDuration() / 1000 / 60); // minutes

      markdown += `## ${session.description}\n\n`;
      markdown += `- **Session ID**: ${session.id}\n`;
      markdown += `- **User**: ${session.userId}\n`;
      markdown += `- **Type**: ${session.type}\n`;
      markdown += `- **Started**: ${session.startTime}\n`;
      markdown += `- **Ended**: ${session.endTime || 'In Progress'}\n`;
      markdown += `- **Duration**: ${duration} minutes\n`;
      markdown += `- **Activities**: ${activities.length}\n\n`;

      if (activities.length > 0) {
        markdown += `### Activities\n\n`;
        activities.forEach((activity) => {
          if (activity) {
            markdown += `- **${activity.timestamp}**: ${activity.description} (${activity.type})\n`;
          }
        });
        markdown += `\n`;
      }

      markdown += `---\n\n`;
    });

    return markdown;
  }

  /**
   * Subscribe to events
   */
  subscribe(listener: Function) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify listeners
   */
  notifyListeners(event, data): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }

  /**
   * Load from storage
   */
  loadFromStorage() {
    try {
      if (typeof localStorage === 'undefined') return;
      const sessionsData = localStorage.getItem(`${(this.options as any).storageKey}:sessions`);
      if (sessionsData) {
        const sessions = JSON.parse(sessionsData);
        this.sessions = new Map(sessions.map((s: any) => [s.id, Session.fromJSON(s)]));
      }

      const activitiesData = localStorage.getItem(`${(this.options as any).storageKey}:activities`);
      if (activitiesData) {
        const activities = JSON.parse(activitiesData);
        this.activities = new Map(activities.map((a: any) => [a.id, ActivitySlug.fromJSON(a)]));
      }

      const currentSessionId = localStorage.getItem(`${this.options.storageKey}:currentSession`);
      if (currentSessionId) {
        this.currentSession = this.sessions.get(currentSessionId) || null;
      }
    } catch (error) {
      console.error('Error loading from storage:', error);
    }
  }

  /**
   * Save to storage
   */
  saveToStorage() {
    if (!this.options) return;
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(
        `${this.options.storagePrefix}_sessions`,
        JSON.stringify(Array.from(this.sessions.values()).map((s) => s.toJSON())),
      );

      localStorage.setItem(
        `${this.options.storagePrefix}_activities`,
        JSON.stringify(Array.from(this.activities.values()).map((a) => a.toJSON())),
      );

      if (this.currentSession) {
        localStorage.setItem(
          `${this.options.storagePrefix}_currentSession`,
          this.currentSession.id,
        );
      } else {
        localStorage.removeItem(`${this.options.storagePrefix}_currentSession`);
      }
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  }
}

// Singleton instance
let instance: LoggrManager | null = null;

/**
 * getLoggr constant
 */
export const getLoggr = (options?: LoggrManagerOptions): LoggrManager => {
  if (!instance) {
    instance = new LoggrManager(options);
  }
  return instance;
};

export default LoggrManager;
